import { GridOptions, GridReadyEvent } from '@ag-grid-community/core'
import { Component, Inject } from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
} from '@helios/ag-grid-config'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import {
  RootState,
  Session,
  selectActiveSession,
  selectSingleFocusedSecurity,
} from '@helios/state-management'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { Store } from '@ngrx/store'
import { NfColumnTypes } from '@northfork/ag-grid'
import { composeFieldPath } from '@northfork/basics'
import { ComponentContainer } from 'golden-layout'
import { Subject, Subscription } from 'rxjs'
import { distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators'
import { PricingKey } from '../panel-key'
import { PriceDataResponse, SourceIdToRank } from './models/price-data'
import { PricingSource } from './models/price-source'
import { PriorityList } from './models/priority-list'
import { PriceSourceSelectionOption } from './pricing-data-menu/pricing-data-menu.component'
import { PricingService } from './services/pricing.service'

const PricingHierarchyGridKey = `${PricingKey}-HierarchyGrid`
const PricingSourceGridKey = `${PricingKey}-SourceGrid`

@Component({
  selector: 'hs-pricing',
  templateUrl: './pricing-panel.component.html',
  styleUrls: ['./pricing-panel.component.scss'],
})
export class PricingPanelComponent extends BasePanelComponent {
  panelKey = PricingKey
  session = this.store.select(selectActiveSession).pipe(distinctUntilChanged())
  accountId?: number
  destroySubject = new Subject<void>()
  private readonly destroySubject$ = new Subject<void>()
  beginDate = ''
  endDate = ''
  lastPricingRunDate = ''
  securityIds: Set<number> = new Set()
  lastPrice = ''
  loadingAllAvailableSources = false
  loadingPricingHierarchyData = false
  loadingpriceData = false
  allAvailableSources: PricingSource[] = []
  pricingHierarchy: PriorityList[] = []
  sessionSub: Subscription | undefined
  securitySub: Subscription | undefined
  priceData: PriceDataResponse[] = []
  securitySelected = false
  chartsEnabled = new FormControl(true)
  pricingSource: Set<string> = new Set()

  priceSourceOption: PriceSourceSelectionOption =
    PriceSourceSelectionOption.HIERARCHY

  private readonly allPriceSourcesByAccountPath = composeFieldPath(
    this.allAvailableSources,
  )
  gridOptionsPricingSource: GridOptions = {
    columnDefs: [
      {
        field: this.allPriceSourcesByAccountPath('id'),
        headerName: 'ID',
        type: NfColumnTypes.Integer,
        valueFormatter: undefined,
      },
      {
        field: this.allPriceSourcesByAccountPath('name'),
      },
    ],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onGridSizeChanged: (params: any) => {
      params.api.sizeColumnsToFit()
    },
    onGridReady: event => this.onPricingSourceGridReady(event),
  }

  private readonly pricingHierarchyPath = composeFieldPath(
    this.pricingHierarchy,
  )
  gridOptionsPricingHierarchy: GridOptions = {
    columnDefs: [
      {
        field: this.pricingHierarchyPath('accountId'),
        headerName: 'Account ID',
        type: NfColumnTypes.Identifier,
      },
      {
        field: this.pricingHierarchyPath('sourceName'),
        headerName: 'Source',
      },
      {
        field: this.pricingHierarchyPath('rank'),
        type: NfColumnTypes.Integer,
        valueFormatter: undefined,
      },
      {
        field: this.pricingHierarchyPath('frequencyName'),
        headerName: 'Frequency',
      },
    ],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onGridSizeChanged: (params: any) => {
      params.api.sizeColumnsToFit()
    },
    onGridReady: event => this.onPricingHierarchyGridReady(event),
  }

  private onPricingHierarchyGridReady(event: GridReadyEvent) {
    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )
    this.savedViewService.manageSavedView(
      event,
      PricingHierarchyGridKey,
      this.destroySubject$,
    )

    enableHeliosCopyBehavior(event.api)
  }

  private onPricingSourceGridReady(event: GridReadyEvent) {
    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this.destroySubject))
      .subscribe(level =>
        enableHeliosGridDensityLevel(event.api, DensityLevel.Compact === level),
      )
    this.savedViewService.manageSavedView(
      event,
      PricingSourceGridKey,
      this.destroySubject$,
    )

    enableHeliosCopyBehavior(event.api)
  }

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly store: Store<RootState>,
    private readonly pricingService: PricingService,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
  ) {
    super(container)
  }

  setBeginDate(beginDate: string) {
    this.beginDate = beginDate
  }
  setEndDate(endDate: string) {
    this.endDate = endDate
    this.setPricingHierarchyData()
  }

  setSecurityIds(securityIds: Set<number>) {
    this.securitySelected = true
    this.securityIds = securityIds
    this.setPriceDataByAccount()
  }

  clearSecurity() {
    this.securitySelected = false
    this.securityIds.clear()
    this.priceData = []
  }

  setPriceSourceOption(priceSourceOption: PriceSourceSelectionOption) {
    this.priceSourceOption = priceSourceOption
    if (this.securityIds.size != 0) {
      this.setPriceDataByAccount()
    }
  }

  ngAfterViewInit() {
    this.sessionSub = this.store
      .select(selectActiveSession)
      .pipe(
        takeUntil(this.destroySubject),
        distinctUntilChanged((a, b) => a?.id === b?.id),
        filter((session): session is Session => session != null),
        tap(session => {
          this.loadPricing(session)
        }),
      )
      .subscribe()
    this.securitySub = this.store
      .select(selectSingleFocusedSecurity)
      .pipe(
        takeUntil(this.destroySubject),
        distinctUntilChanged(),
        tap(securityId => {
          if (securityId) {
            const secIds = new Set<number>()
            secIds.add(Number(securityId))
            this.setSecurityIds(secIds)
          } else {
            this.clearSecurity()
          }
        }),
      )
      .subscribe()
  }

  loadPricing(session?: Session) {
    if (session) {
      this.accountId = session.loadedAccountIds[0]
      this.setAllPriceSourcesByAccount()
      this.setPricingHierarchyData()
    }
  }

  ngOnDestroy(): void {
    this.destroySubject.next()
    this.destroySubject.complete()
  }

  setAllPriceSourcesByAccount() {
    if (this.accountId) {
      this.loadingAllAvailableSources = true
      this.pricingService
        .getAllAvailableSources(this.accountId)
        .subscribe(pricingSources => {
          this.allAvailableSources = pricingSources
          this.loadingAllAvailableSources = false
        })
    }
  }

  setAccountIdAndSecurityId(accountId: number) {
    this.accountId = accountId
    this.setPricingHierarchyData()
    if (this.accountId && this.securityIds.size > 0) {
      this.setPriceDataByAccount()
    }
  }

  setPricingHierarchyData() {
    if (this.accountId && this.endDate) {
      this.loadingPricingHierarchyData = true
      this.pricingService
        .getPriorityList(this.accountId, this.endDate)
        .subscribe(priorityList => {
          this.pricingHierarchy = []
          if (this.accountId) {
            if (priorityList?.[this.accountId] != undefined)
              priorityList[this.accountId].forEach(priorityElement => {
                if (this.accountId) priorityElement.accountId = this.accountId
                if (priorityElement.id == 4)
                  priorityElement.sourceName = 'Custody'
                this.pricingHierarchy.push(priorityElement)
              })
            if (this.securityIds.size != 0) {
              this.setPriceDataByAccount()
            }
          }
          this.loadingPricingHierarchyData = false
        })
    }
  }

  setPriceDataByAccount() {
    if (this.accountId && this.endDate && this.beginDate && this.securityIds) {
      this.loadingpriceData = true
      const sourceIdToRank: SourceIdToRank = {}
      const sourceIds: Set<number> = new Set()
      this.pricingSource.clear()
      this.lastPricingRunDate = ''
      this.lastPrice = ''

      if (this.priceSourceOption === PriceSourceSelectionOption.HIERARCHY) {
        this.pricingHierarchy.forEach(priorityList => {
          sourceIdToRank[priorityList.id] = priorityList.rank
          sourceIds.add(priorityList.id)
          this.pricingSource.add(
            `${priorityList.sourceName}(${priorityList.rank})`,
          )
        })
      } else {
        this.allAvailableSources.forEach(source => {
          sourceIds.add(source.id)
          this.pricingSource.add(source.name)
        })
      }

      this.pricingService
        .getCompletePricingData(
          this.accountId,
          Array.from(this.securityIds.values()),
          Array.from(sourceIds.values()),
          this.beginDate,
          this.endDate,
          sourceIdToRank,
        )
        .subscribe(pricingData => {
          this.priceData = pricingData
          const compDate = new Date(this.beginDate)
          compDate.setHours(0, 0, 0, 0)
          this.priceData.forEach((data, index) => {
            if (new Date(data.pricingDate) < compDate) {
              this.lastPricingRunDate = data.pricingDate
              this.lastPrice = data.price.toString()
              this.priceData.splice(index, 1)
            }
          })
          this.loadingpriceData = false
        })
    }
  }
}
